import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Check, Eye, EyeOff, Youtube, ExternalLink } from "lucide-react";
import { api, formatApiError, resolveMediaUrl, getLiveMediaInfo } from "@/lib/api";
import FileUpload from "@/components/admin/FileUpload";

const empty = { title: "", youtube_url: "", thumbnail: "", description: "", published: true, posted_at: "" };

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const LivePreview = ({ url, thumbnail, title }) => {
  const info = getLiveMediaInfo(url);
  if (info?.embed) {
    return (
      <iframe
        title={title}
        src={info.embed}
        className="w-full h-full min-h-[160px] border-0"
        allow="encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  }
  if (thumbnail) {
    return <img src={resolveMediaUrl(thumbnail)} alt={title} className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/70 p-4 text-center">
      <Youtube size={36} />
      <span className="text-xs font-semibold uppercase tracking-wider">{info?.label || "Live"}</span>
    </div>
  );
};

const AdminLives = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/lives").then(({ data }) => setItems(Array.isArray(data) ? data : []));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing("new");
    setForm({ ...empty, posted_at: toLocalInput(new Date().toISOString()) });
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      title: c.title || "",
      youtube_url: c.youtube_url || "",
      thumbnail: c.thumbnail || "",
      description: c.description || "",
      published: !!c.published,
      posted_at: toLocalInput(c.posted_at || c.created_at),
    });
  };

  const save = async () => {
    if (!form.title || !form.youtube_url) {
      toast.error("Judul dan link live wajib diisi");
      return;
    }
    const info = getLiveMediaInfo(form.youtube_url);
    if (!info?.valid) {
      toast.error("Link harus YouTube atau TikTok yang valid");
      return;
    }
    try {
      const payload = {
        title: form.title,
        youtube_url: form.youtube_url.trim(),
        thumbnail: form.thumbnail || "",
        description: form.description || "",
        published: !!form.published,
        posted_at: form.posted_at ? new Date(form.posted_at).toISOString() : new Date().toISOString(),
      };
      if (editing === "new") await api.post("/lives", payload);
      else await api.put(`/lives/${editing}`, payload);
      toast.success("Berhasil disimpan");
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail || e.response?.data));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus live ini?")) return;
    await api.delete(`/lives/${id}`);
    toast.success("Terhapus");
    load();
  };

  const toggle = async (c) => {
    await api.put(`/lives/${c.id}`, {
      title: c.title,
      youtube_url: c.youtube_url,
      thumbnail: c.thumbnail || "",
      description: c.description || "",
      published: !c.published,
      posted_at: c.posted_at,
    });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-zinc-900">Live</h1>
          <p className="mt-1 text-sm text-zinc-500">Share link live YouTube atau TikTok. Tampil di website.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-maroon-800 text-white font-semibold">
          <Plus size={16} /> Tambah Live
        </button>
      </div>

      <div className="space-y-4">
        {items.map((c) => {
          const info = getLiveMediaInfo(c.youtube_url);
          return (
            <div key={c.id} className="bg-white rounded-2xl border border-zinc-200 overflow-hidden grid md:grid-cols-[280px_1fr]">
              <div className="aspect-video md:aspect-auto md:min-h-[160px] bg-zinc-900">
                <LivePreview url={c.youtube_url} thumbnail={c.thumbnail} title={c.title} />
              </div>
              <div className="p-5 flex flex-col justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${info?.platform === "tiktok" ? "bg-zinc-900 text-white" : "bg-red-100 text-red-700"}`}>
                      {info?.label || "Live"}
                    </span>
                    <span>
                      {c.posted_at ? new Date(c.posted_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                    </span>
                  </div>
                  <h3 className="mt-1 text-lg font-display font-bold text-zinc-900">{c.title}</h3>
                  {c.description && <p className="mt-2 text-sm text-zinc-600 line-clamp-2">{c.description}</p>}
                  <a href={c.youtube_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-maroon-700 break-all hover:underline">
                    {c.youtube_url} <ExternalLink size={12} />
                  </a>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className={`text-xs font-semibold ${c.published ? "text-maroon-700" : "text-zinc-400"}`}>
                    {c.published ? "Aktif" : "Draft"}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => toggle(c)} className="p-2 rounded-lg hover:bg-zinc-100">{c.published ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                    <button onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-zinc-100"><Pencil size={14} /></button>
                    <button onClick={() => del(c.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="p-12 text-center text-zinc-500 bg-white rounded-2xl border border-zinc-200">
            Belum ada live. Klik “Tambah Live” lalu paste link YouTube atau TikTok.
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-display font-bold">{editing === "new" ? "Tambah Live" : "Edit Live"}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-zinc-100"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Judul *">
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
              </Field>
              <Field label="Link YouTube / TikTok Live *">
                <input value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                  placeholder="YouTube: youtube.com/... · TikTok: tiktok.com/@user/live atau /video/..."
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
                <p className="mt-1 text-xs text-zinc-500">
                  YouTube video/live, atau TikTok live (`/@akun/live`) dan video TikTok.
                </p>
              </Field>
              <FileUpload label="Thumbnail (opsional, disarankan untuk TikTok Live)" value={form.thumbnail} onChange={(v) => setForm({ ...form, thumbnail: v })} folder="lives" aspect="wide" />
              <Field label="Tanggal tayang / upload">
                <input type="datetime-local" value={form.posted_at} onChange={(e) => setForm({ ...form, posted_at: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
              </Field>
              <Field label="Keterangan (opsional)">
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 outline-none focus:border-maroon-700" />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                Tampilkan di website
              </label>
            </div>
            <div className="sticky bottom-0 bg-white flex justify-end gap-3 p-6 border-t">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full border font-semibold">Batal</button>
              <button onClick={save} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-maroon-800 text-white font-semibold">
                <Check size={16} /> Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-semibold text-zinc-800 mb-2">{label}</label>
    {children}
  </div>
);

export default AdminLives;
